import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatDateTime, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, Copy, Wand2, Mail, MessageSquare, X, UserCircle, Megaphone } from "lucide-react";
import { toast } from "sonner";
import CampaignWizard from "@/components/CampaignWizard";
import { PANEL_CLS, ROW_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS, StatusPill } from "@/components/premium";

const PURPOSES = [
  { v: "takk_for_besoeket", l: "Takk for besøket" },
  { v: "paaminnelse_time", l: "Påminnelse om time" },
  { v: "inaktiv_kunde", l: "Vekk inaktiv kunde" },
  { v: "bursdag_kampanje", l: "Bursdagshilsen" },
  { v: "be_om_anmeldelse", l: "Be om anmeldelse" },
];
const TONES = [
  { v: "vennlig", l: "Vennlig" }, { v: "profesjonell", l: "Profesjonell" },
  { v: "kort", l: "Kort" }, { v: "varm", l: "Varm" },
];

export default function AIMessages() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Campaign wizard deep-link: Dashboard quick action sends users here
  // with `?type=campaign` so the modal opens automatically. Stays open
  // until the user closes it. The URL param is consumed on mount so
  // refresh/back-nav doesn't re-trigger.
  const [campaignOpen, setCampaignOpen] = useState(false);
  useEffect(() => {
    if (searchParams.get("type") === "campaign") {
      setCampaignOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("type");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);
  const [form, setForm] = useState({
    purpose: "takk_for_besoeket", channel: "sms", tone: "vennlig",
    customer_name: "", service_name: "", custom_hint: "",
  });
  const [generated, setGenerated] = useState(null);
  const [loadingGen, setLoadingGen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [services, setServices] = useState([]);
  // Plan-gating: AI-generering er Pro/Premium/Demo. Basic ser et oppgraderingskort
  // i stedet for generatoren, men beholder full tilgang til utkast/sendt-listen
  // så de fortsatt kan jobbe med manuelle utkast (auto-genererte ved completed
  // booking) — backend POST /api/ai/generate-followup returnerer uansett 402.
  const [biz, setBiz] = useState(null);
  // Prefill context from another page (Dashboard, CustomerDetail, Customers list)
  const [prefillCtx, setPrefillCtx] = useState(null);

  const load = async () => {
    try {
      const [f, s, b] = await Promise.all([
        api.get("/followups"),
        api.get("/services"),
        api.get("/business"),
      ]);
      setMessages(f.data);
      setServices(s.data);
      setBiz(b.data);
    } catch { toast.error("Kunne ikke laste"); }
  };
  useEffect(() => { load(); }, []);

  const isBasicPlan = biz?.plan === "basic";

  // Apply prefill from navigation state
  useEffect(() => {
    const customerId = location.state?.customerId;
    if (!customerId) return;
    (async () => {
      try {
        const res = await api.get(`/customers/${customerId}`);
        const c = res.data.customer;
        const bookings = res.data.bookings || [];
        // Pick most recent completed booking
        const lastCompleted = bookings.find((b) => b.status === "completed");
        const lastVisitDate = lastCompleted?.start_at;
        // Infer purpose from recency
        let purpose = "takk_for_besoeket";
        if (lastVisitDate) {
          const days = (Date.now() - new Date(lastVisitDate).getTime()) / 86400000;
          if (days > 60) purpose = "inaktiv_kunde";
          else if (days < 7) purpose = "takk_for_besoeket";
        } else if (c.total_visits === 0) {
          purpose = "takk_for_besoeket";
        }
        // Channel: prefer email if available, else SMS
        const channel = c.email ? "email" : "sms";
        setForm((prev) => ({
          ...prev,
          purpose,
          channel,
          customer_name: c.name,
          service_name: lastCompleted?.service_name || prev.service_name,
        }));
        setPrefillCtx({
          customer: c,
          lastVisit: lastVisitDate,
          lastService: lastCompleted?.service_name,
        });
        setGenerated(null);
      } catch {
        toast.error("Kunne ikke hente kundedata");
      }
    })();
    // Clear navigation state so refresh doesn't re-trigger
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const clearPrefill = () => {
    setPrefillCtx(null);
    setForm((prev) => ({ ...prev, customer_name: "", service_name: "" }));
  };

  const generate = async () => {
    setLoadingGen(true); setGenerated(null);
    try {
      const res = await api.post("/ai/generate-followup", form);
      setGenerated(res.data);
      toast.success("Melding generert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke generere");
    } finally { setLoadingGen(false); }
  };

  const saveDraft = async () => {
    if (!generated) return toast.error("Ingen melding");
    if (!form.customer_name) return toast.error("Angi kundenavn");
    try {
      await api.post("/followups", {
        customer_name: form.customer_name,
        channel: form.channel,
        subject: generated.subject || null,
        body: generated.body,
        tone: form.tone,
        purpose: form.purpose,
      });
      toast.success("Utkast lagret");
      load();
    } catch { toast.error("Kunne ikke lagre"); }
  };

  const send = async (id) => {
    try { await api.post(`/followups/${id}/send`); toast.success("Sendt (mock)"); load(); }
    catch { toast.error("Kunne ikke sende"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Kopiert"); };

  return (
    <div className="space-y-6" data-testid="ai-messages-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className={PAGE_EYEBROW_CLS}>AI Meldinger</p>
          <h1 className={PAGE_H1_CLS}>Automatiske oppfølginger</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Generer personlige SMS/e-post på norsk med ett klikk.</p>
        </div>
        {!isBasicPlan && (
          <Button
            onClick={() => setCampaignOpen(true)}
            className={PRIMARY_BTN_CLS}
            data-testid="open-campaign-wizard"
          >
            <Megaphone className="w-4 h-4" strokeWidth={1.75} />
            Opprett kampanje
          </Button>
        )}
      </div>

      <CampaignWizard
        open={campaignOpen}
        onOpenChange={setCampaignOpen}
        onDone={() => load()}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        {isBasicPlan ? (
          <Card
            className={`${PANEL_CLS} bg-gradient-to-br from-emerald-500/10 via-card to-card lg:col-span-2`}
            data-testid="ai-basic-gate"
          >
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-5">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-500 shrink-0">
                <Sparkles className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={PAGE_EYEBROW_CLS}>Pro-funksjon</p>
                <h2 className="font-heading text-xl sm:text-2xl font-bold mt-1">AI-genererte oppfølgingsmeldinger</h2>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                  Pro og Premium kan generere personlige SMS- og e-postmeldinger på norsk automatisk
                  — "Takk for besøket", påminnelser, vekk inaktive kunder, og mer.
                  Du kan fortsatt skrive og sende oppfølgingsmeldinger manuelt fra utkast-listen nedenfor.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    onClick={() => { window.location.href = "/#priser"; }}
                    className={PRIMARY_BTN_CLS}
                    data-testid="ai-upgrade-cta"
                  >
                    Se Pro-pakken
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    className="rounded-2xl h-10"
                  >
                    Tilbake til dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
        <Card className={PANEL_CLS}>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Wand2 className="w-4 h-4 text-emerald-500" /> Generer melding</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {prefillCtx && (
              <div
                className={`flex items-start gap-3 ${ROW_CLS} p-3`}
                data-testid="ai-prefill-chip"
              >
                <div className="w-9 h-9 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-500 shrink-0">
                  <UserCircle className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    Forhåndsutfylt for {prefillCtx.customer.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {prefillCtx.lastVisit
                      ? `Sist besøk: ${formatDate(prefillCtx.lastVisit)}${prefillCtx.lastService ? ` · ${prefillCtx.lastService}` : ""}`
                      : "Ingen tidligere besøk registrert"}
                    {prefillCtx.customer.email
                      ? " · har e-post"
                      : prefillCtx.customer.phone
                        ? " · har telefon"
                        : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearPrefill}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Fjern forhåndsutfylling"
                  data-testid="ai-prefill-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div>
              <Label>Formål</Label>
              <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="ai-purpose-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PURPOSES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kanal</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger className="rounded-xl mt-1.5" data-testid="ai-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">E-post</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tone</Label>
                <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                  <SelectTrigger className="rounded-xl mt-1.5" data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Kundens navn</Label>
              <Input className="rounded-xl mt-1.5" value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="F.eks. Kari" data-testid="ai-customer-input" />
            </div>
            <div>
              <Label>Tjeneste (valgfritt)</Label>
              <Select value={form.service_name || ""} onValueChange={(v) => setForm({ ...form, service_name: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="ai-service-select">
                  <SelectValue placeholder="Velg tjeneste" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ekstra hint (valgfritt)</Label>
              <Textarea className="rounded-xl mt-1.5" rows={2} value={form.custom_hint}
                onChange={(e) => setForm({ ...form, custom_hint: e.target.value })}
                placeholder="F.eks. 'Nevn at vi har ledig tid neste uke'" data-testid="ai-hint-input" />
            </div>
            <Button onClick={generate} disabled={loadingGen} className={`w-full ${PRIMARY_BTN_CLS} h-11`} data-testid="ai-generate-btn">
              <Sparkles className="w-4 h-4" strokeWidth={2} />
              {loadingGen ? "Genererer..." : "Generer med AI"}
            </Button>
          </CardContent>
        </Card>

        <Card className={PANEL_CLS}>
          <CardHeader><CardTitle className="font-heading">Forhåndsvisning</CardTitle></CardHeader>
          <CardContent>
            {loadingGen ? (
              <div className="space-y-3" data-testid="ai-preview-loading">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-500 animate-pulse" />
                  <span className="text-sm text-muted-foreground">Genererer melding på norsk...</span>
                </div>
                <div className="space-y-2 pt-2">
                  <div className="h-3 w-full bg-emerald-500/10 rounded animate-pulse" />
                  <div className="h-3 w-5/6 bg-emerald-500/10 rounded animate-pulse" />
                  <div className="h-3 w-4/6 bg-emerald-500/10 rounded animate-pulse" />
                  <div className="h-3 w-3/4 bg-emerald-500/10 rounded animate-pulse" />
                </div>
              </div>
            ) : !generated ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="ai-preview-empty">
                <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-40 text-emerald-500" strokeWidth={1.5} />
                <p>Fyll ut skjemaet og klikk "Generer".</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="ai-preview">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {form.channel === "sms" ? <MessageSquare className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  <span className="uppercase tracking-widest font-semibold">{form.channel}</span>
                </div>
                {generated.subject && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Emne</p>
                    <p className="font-semibold">{generated.subject}</p>
                  </div>
                )}
                <div className={`p-4 ${ROW_CLS} whitespace-pre-wrap text-sm`} data-testid="ai-preview-body">
                  {generated.body}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-2xl h-10 flex-1" onClick={() => copy((generated.subject ? `Emne: ${generated.subject}\n\n` : "") + generated.body)} data-testid="ai-copy-btn">
                    <Copy className="w-4 h-4 mr-1" /> Kopier
                  </Button>
                  <Button className={`flex-1 ${PRIMARY_BTN_CLS} h-10`} onClick={saveDraft} data-testid="ai-save-draft-btn">
                    Lagre utkast
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>

      <Card className={PANEL_CLS}>
        <CardHeader><CardTitle className="font-heading">Meldinger</CardTitle></CardHeader>
        <CardContent>
          <Tabs defaultValue="draft" className="w-full">
            <TabsList className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/15">
              <TabsTrigger value="draft" className="rounded-lg data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-600" data-testid="tab-draft">Utkast</TabsTrigger>
              <TabsTrigger value="sent" className="rounded-lg data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-600" data-testid="tab-sent">Sendt</TabsTrigger>
            </TabsList>
            {["draft", "sent"].map((st) => (
              <TabsContent key={st} value={st} className="mt-4">
                {messages.filter((m) => m.status === st).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">Ingen meldinger her.</p>
                ) : (
                  <ul className="space-y-2">
                    {messages.filter((m) => m.status === st).map((m) => (
                      <li key={m.id} className={`${ROW_CLS} p-3.5 flex items-start justify-between gap-4`} data-testid={`message-${st}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold">{m.customer_name}</p>
                            <Badge variant="outline" className="rounded-full text-[10px] border-emerald-500/30">{m.channel.toUpperCase()}</Badge>
                            <StatusPill status={st === "draft" ? "draft" : "sent"} compact />
                          </div>
                          {m.subject && <p className="text-xs text-muted-foreground font-semibold">{m.subject}</p>}
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{m.body}</p>
                          {m.sent_at && <p className="text-[11px] text-muted-foreground mt-1">Sendt: {formatDateTime(m.sent_at)}</p>}
                        </div>
                        {st === "draft" && (
                          <Button size="sm" className={`${PRIMARY_BTN_CLS} h-9 px-3 text-sm`} onClick={() => send(m.id)} data-testid="send-message-btn">
                            <Send className="w-3.5 h-3.5" /> Send
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
